import { supabase } from './supabaseClient';
import { logger } from './logger';

/**
 * Serviço de gerenciamento de assinaturas
 * Integra dados de public.users e subscriptions
 */

// Definição dos planos e seus limites
export const PLANS = {
  free: {
    id: 'free',
    name: 'Gratuito',
    maxConversations: 3,
    maxExportsPerMonth: 5,
    hasQuickTopics: false,
    hasPrioritySupport: false,
    hasContractTemplates: false,
    hasNewsletter: false,
    hasApiAccess: false,
    historyDays: 7,
  },
  basic: {
    id: 'basic',
    name: 'Básico',
    maxConversations: 12,
    maxExportsPerMonth: 20,
    hasQuickTopics: false,
    hasPrioritySupport: false,
    hasContractTemplates: false,
    hasNewsletter: false,
    hasApiAccess: false,
    historyDays: 30,
  },
  pro: {
    id: 'pro',
    name: 'Profissional',
    maxConversations: -1, // ilimitado
    maxExportsPerMonth: 50,
    hasQuickTopics: true,
    hasPrioritySupport: true,
    hasContractTemplates: true,
    hasNewsletter: false,
    hasApiAccess: false,
    historyDays: 180,
  },
  master: {
    id: 'master',
    name: 'Master',
    maxConversations: -1, // ilimitado
    maxExportsPerMonth: -1, // ilimitado
    hasQuickTopics: true,
    hasPrioritySupport: true,
    hasContractTemplates: true,
    hasNewsletter: true,
    hasApiAccess: true,
    historyDays: -1, // ilimitado
  },
};

/**
 * Garante que o usuário existe na tabela public.users
 * Cria automaticamente se não existir (fallback para quando não há trigger)
 * Usa dados do sessionStorage se disponíveis (salvos durante registro)
 * @param {object} authUser - Usuário do Supabase Auth
 */
async function ensureUserExists(authUser) {
  try {
    // Verifica se usuário já existe
    const { data: existingUser, error: checkError } = await supabase
      .from('users')
      .select('id')
      .eq('id', authUser.id)
      .maybeSingle();

    if (checkError && checkError.code !== 'PGRST116') {
      logger.warn('⚠️ Erro ao verificar usuário:', checkError.message);
      return null;
    }

    if (existingUser) {
      // Limpar dados pendentes do sessionStorage
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('pendingUserData');
        sessionStorage.removeItem('pendingVerificationEmail');
        sessionStorage.removeItem('pendingUserId');
      }
      return existingUser; // Usuário já existe
    }

    logger.log('🔄 Criando usuário em public.users (primeiro acesso)...');

    // Tentar recuperar dados do registro salvos no sessionStorage
    let pendingData = null;
    if (typeof window !== 'undefined') {
      const savedData = sessionStorage.getItem('pendingUserData');
      if (savedData) {
        try {
          pendingData = JSON.parse(savedData);
          logger.log('📦 Usando dados salvos do registro:', pendingData);
        } catch (e) {
          logger.warn('⚠️ Erro ao parsear dados pendentes:', e);
        }
      }
    }

    // Cria o usuário com dados do sessionStorage ou auth.users
    const { data: newUser, error: insertError } = await supabase
      .from('users')
      .insert({
        id: authUser.id,
        email: authUser.email,
        full_name: pendingData?.full_name || authUser.user_metadata?.full_name || '',
        company: authUser.user_metadata?.company || '',
        type_business: authUser.user_metadata?.type_business || '',
        phone: pendingData?.phone || authUser.user_metadata?.phone || '',
        document_type: pendingData?.document_type || authUser.user_metadata?.document_type || '',
        document: pendingData?.document || authUser.user_metadata?.document || '',
        is_active: true,
      })
      .select()
      .single();

    if (insertError) {
      logger.error('❌ Erro ao criar usuário:', insertError.message, insertError.code);
      return null;
    }

    logger.log('✅ Usuário criado em public.users');
    
    // Limpar dados pendentes do sessionStorage após criação bem-sucedida
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('pendingUserData');
      sessionStorage.removeItem('pendingVerificationEmail');
      sessionStorage.removeItem('pendingUserId');
      logger.log('🧹 Dados pendentes limpos do sessionStorage');
    }

    // NÃO criar assinatura FREE aqui - o Stripe/webhook vai criar a assinatura correta
    // Se o usuário fez checkout, ele terá uma assinatura paga
    // Se não fez, vai para a página de pricing
    logger.log('ℹ️ Assinatura será criada via webhook do Stripe');

    return newUser;
  } catch (error) {
    logger.error('❌ Erro em ensureUserExists:', error);
    return null;
  }
}

/**
 * Busca informações completas do usuário e assinatura
 * @param {string} userId - ID do usuário
 * @returns {Promise<{user, subscription, plan, limits}>}
 */
export async function getUserSubscriptionData(userId) {
  try {
    // Primeiro, garante que o usuário existe
    // Busca o authUser para ter acesso aos metadados
    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (authUser) {
      await ensureUserExists(authUser);
    }

    // Buscar dados do usuário em public.users
    // Usa maybeSingle() para evitar erro 404 quando usuário ainda não existe
    const { data: user, error: userError } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .maybeSingle();

    // Se não encontrou usuário, retorna dados mínimos do authUser
    if (!user) {
      logger.warn('⚠️ Usuário não encontrado em public.users, usando dados do auth');
      const minimalUser = {
        id: userId,
        email: authUser?.email || '',
        full_name: authUser?.user_metadata?.full_name || '',
      };
      
      return {
        user: minimalUser,
        subscription: null,
        plan: PLANS.basic,
        usage: {
          messagesThisMonth: 0,
          tokensUsed: 0,
          conversationCount: 0,
          exportsThisMonth: 0,
        },
        limits: {
          canCreateConversation: true,
          canExport: true,
          hasQuickTopics: false,
          hasPrioritySupport: false,
          hasContractTemplates: false,
          hasNewsletter: false,
          hasApiAccess: false,
        },
      };
    }

    if (userError && userError.code !== 'PGRST116') {
      logger.warn('Erro ao buscar usuário:', userError);
    }

    // Buscar assinatura ativa do usuário
    const { data: subscription, error: subError } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', userId)
      .in('status', ['active', 'trialing']) // Aceita active ou trialing
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (subError && subError.code !== 'PGRST116') {
      logger.warn('Erro ao buscar subscription:', subError);
    }

    logger.log('📊 Subscription encontrada:', subscription || 'nenhuma');

    // Determinar o plano (padrão: basic se não houver assinatura)
    const planId = subscription?.plan_id || 'basic';
    const plan = PLANS[planId] || PLANS.basic;

    // Buscar métricas de uso do mês atual
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    const { data: usageMetrics, error: usageError } = await supabase
      .from('usage_metrics')
      .select('messages_sent, tokens_used, exports_count')
      .eq('user_id', userId)
      .gte('date', firstDayOfMonth.toISOString().split('T')[0])
      .lte('date', lastDayOfMonth.toISOString().split('T')[0]);

    if (usageError && usageError.code !== 'PGRST116') {
      logger.warn('Erro ao buscar métricas de uso:', usageError);
    }

    // Calcular totais de uso
    const totalMessages = usageMetrics?.reduce((sum, metric) => sum + (metric.messages_sent || 0), 0) || 0;
    const totalTokens = usageMetrics?.reduce((sum, metric) => sum + (metric.tokens_used || 0), 0) || 0;
    const totalExports = usageMetrics?.reduce((sum, metric) => sum + (metric.exports_count || 0), 0) || 0;

    // Contar conversas salvas
    const { count: conversationCount, error: countError } = await supabase
      .from('chat_topics')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId);

    if (countError && countError.code !== 'PGRST116') {
      logger.warn('Erro ao contar conversas:', countError);
    }

    return {
      user,
      subscription,
      plan,
      usage: {
        messagesThisMonth: totalMessages,
        tokensUsed: totalTokens,
        conversationCount: conversationCount || 0,
        exportsThisMonth: totalExports,
      },
      limits: {
        canCreateConversation: plan.maxConversations === -1 || (conversationCount || 0) < plan.maxConversations,
        canExport: plan.maxExportsPerMonth === -1 || totalExports < plan.maxExportsPerMonth,
        hasQuickTopics: plan.hasQuickTopics,
        hasPrioritySupport: plan.hasPrioritySupport,
        hasContractTemplates: plan.hasContractTemplates,
        hasNewsletter: plan.hasNewsletter,
        hasApiAccess: plan.hasApiAccess,
      },
    };
  } catch (error) {
    logger.error('Erro ao buscar dados de assinatura:', error);
    throw error;
  }
}

/**
 * Verifica se o usuário pode criar uma nova conversa
 * @param {string} userId - ID do usuário
 * @returns {Promise<boolean>}
 */
export async function canCreateConversation(userId) {
  try {
    const { limits } = await getUserSubscriptionData(userId);
    return limits.canCreateConversation;
  } catch (error) {
    logger.error('Erro ao verificar limite de conversas:', error);
    return false;
  }
}

/**
 * Verifica se o usuário pode exportar
 * @param {string} userId - ID do usuário
 * @returns {Promise<boolean>}
 */
export async function canExport(userId) {
  try {
    const { limits } = await getUserSubscriptionData(userId);
    return limits.canExport;
  } catch (error) {
    logger.error('Erro ao verificar limite de exports:', error);
    return false;
  }
}

/**
 * Atualiza o status de primeira visualização do modal de boas-vindas
 * @param {string} userId - ID do usuário
 * @returns {Promise<void>}
 */
export async function markWelcomeModalAsSeen(userId) {
  try {
    const { error } = await supabase
      .from('users')
      .update({ has_seen_welcome_modal: true, updated_at: new Date().toISOString() })
      .eq('id', userId);

    if (error) throw error;
  } catch (error) {
    logger.error('Erro ao marcar modal de boas-vindas como visto:', error);
    throw error;
  }
}

/**
 * Verifica se deve mostrar o modal de boas-vindas
 * @param {object} user - Dados do usuário
 * @param {object} subscription - Dados da assinatura
 * @returns {boolean}
 */
export function shouldShowWelcomeModal(user, subscription) {
  // Mostra se:
  // 1. Usuário nunca viu o modal
  // 2. Tem uma assinatura ativa
  // 3. A assinatura foi criada recentemente (últimas 24h)
  if (!user || !subscription) return false;
  if (user.has_seen_welcome_modal) return false;
  if (subscription.status !== 'active') return false;

  const subscriptionDate = new Date(subscription.created_at);
  const now = new Date();
  const hoursSinceSubscription = (now - subscriptionDate) / (1000 * 60 * 60);

  return hoursSinceSubscription < 24;
}

/**
 * Registra uma exportação no sistema
 * @param {string} userId - ID do usuário
 * @returns {Promise<void>}
 */
export async function trackExport(userId) {
  try {
    const today = new Date().toISOString().split('T')[0];
    
    // Tenta atualizar o registro existente do dia
    const { data: existing, error: fetchError } = await supabase
      .from('usage_metrics')
      .select('*')
      .eq('user_id', userId)
      .eq('date', today)
      .maybeSingle();

    if (fetchError && fetchError.code !== 'PGRST116') {
      throw fetchError;
    }

    if (existing) {
      // Atualiza o contador de exportações
      const { error: updateError } = await supabase
        .from('usage_metrics')
        .update({
          exports_count: (existing.exports_count || 0) + 1,
          updated_at: new Date().toISOString(),
        })
        .eq('id', existing.id);

      if (updateError) throw updateError;
      logger.log('✅ Exportação registrada no banco:', (existing.exports_count || 0) + 1);
    } else {
      // Cria novo registro
      const { error: insertError } = await supabase
        .from('usage_metrics')
        .insert({
          user_id: userId,
          date: today,
          exports_count: 1,
          messages_sent: 0,
          tokens_used: 0,
        });

      if (insertError) throw insertError;
      logger.log('✅ Exportação registrada no banco: 1');
    }
  } catch (error) {
    logger.error('Erro ao registrar exportação:', error);
    throw error;
  }
}

/**
 * Busca o total de exportações do mês atual
 * @param {string} userId - ID do usuário
 * @returns {Promise<number>}
 */
export async function getExportsThisMonth(userId) {
  try {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    const { data: usageMetrics, error } = await supabase
      .from('usage_metrics')
      .select('exports_count')
      .eq('user_id', userId)
      .gte('date', firstDayOfMonth.toISOString().split('T')[0])
      .lte('date', lastDayOfMonth.toISOString().split('T')[0]);

    if (error && error.code !== 'PGRST116') {
      logger.warn('Erro ao buscar exportações:', error);
      return 0;
    }

    const total = usageMetrics?.reduce((sum, metric) => sum + (metric.exports_count || 0), 0) || 0;
    return total;
  } catch (error) {
    logger.error('Erro ao buscar exportações do mês:', error);
    return 0;
  }
}

/**
 * Formata o nome do plano para exibição
 * @param {string} planId - ID do plano
 * @returns {string}
 */
export function getPlanDisplayName(planId) {
  return PLANS[planId]?.name || 'Plano Desconhecido';
}

/**
 * Formata a data de renovação
 * @param {string} dateString - Data em string ISO
 * @returns {string}
 */
export function formatRenewalDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('pt-BR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

/**
 * Calcula dias restantes até renovação
 * @param {string} dateString - Data em string ISO
 * @returns {number}
 */
export function getDaysUntilRenewal(dateString) {
  if (!dateString) return 0;
  const renewalDate = new Date(dateString);
  const now = new Date();
  const diffTime = renewalDate - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays > 0 ? diffDays : 0;
}
